import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { fetchManagers } from "../../store/slices/leaveSlice";
import { RegularizationPayload } from "../../models/regularize.model";

interface AttendanceRegularizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  onSubmit: (data: RegularizationPayload) => void;
}

const AttendanceRegularizeModal: React.FC<AttendanceRegularizeModalProps> = ({
  isOpen,
  onClose,
  date,
  onSubmit,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const managers = useSelector((state: RootState) => state.leave.managers);
  const managerId = useSelector((state: RootState) => state.auth.manager);

  // Load managers when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchManagers());
    }
  }, [isOpen, dispatch]);

  // Get manager name
  const managerName = managers.find((m) => m.id === managerId)?.name || "N/A";

  const [startTime, setStartTime] = useState("09:30");
  const [endTime, setEndTime] = useState("18:30");
  const [ccInput, setCcInput] = useState("");
  const [ccDropdownOpen, setCcDropdownOpen] = useState(false);
  const [ccTo, setCcTo] = useState<string[]>([]);
  const [reason, setReason] = useState("");

  // Reset form on modal close
  useEffect(() => {
    if (!isOpen) {
      setStartTime("09:30");
      setEndTime("18:30"); 
      setCcInput("");
      setCcTo([]);
      setReason("");
      setCcDropdownOpen(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!startTime || !endTime) {
      alert("Please select start and end times");
      return;
    }
    if (!reason.trim()) {
      alert("Please provide a reason");
      return;
    }

    onSubmit({
      date,
      startTime,
      endTime,
      applyingTo: managerId || "",
      ccTo,
      reason,
    });

    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-4">
          Regularize Attendance for {new Date(date).toDateString()}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-4">
            {/* Start Time */}
            <label className="flex-1 flex flex-col text-xs font-medium text-gray-500">
              Start Time
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm cursor-pointer"
              />
            </label>

            {/* End Time */}
            <label className="flex-1 flex flex-col text-xs font-medium text-gray-500">
              End Time
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm cursor-pointer"
              />
            </label>
          </div>
          {/* Applying To */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              APPLYING TO
            </label>
            <input
              type="text"
              value={managerName}
              readOnly
              className="w-full border border-gray-200 bg-gray-100 rounded-lg px-4 py-3 text-sm text-gray-700"
            />
          </div>

          {/* CC To */}
          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              CC TO (OPTIONAL)
            </label>

            <input
              type="text"
              value={ccInput}
              onFocus={() => setCcDropdownOpen(true)}
              onBlur={() => setTimeout(() => setCcDropdownOpen(false), 200)}
              onChange={(e) => setCcInput(e.target.value)}
              placeholder="Search manager"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            />

            {/* Dropdown */}
            {ccDropdownOpen && (
              <div className="absolute bg-white z-10 border rounded-md shadow-lg w-full max-h-48 overflow-y-auto mt-1">
                {managers
                  .filter(
                    (m) =>
                      m.name.toLowerCase().includes(ccInput.toLowerCase()) &&
                      !ccTo.includes(m.id) &&
                      m.id !== managerId
                  )
                  .map((manager) => (
                    <div
                      key={manager.id}
                      onMouseDown={() => {
                        setCcTo((prev) => [...prev, manager.id]);
                        setCcInput("");
                      }}
                      className="px-4 py-2 hover:bg-blue-100 cursor-pointer text-sm"
                    >
                      {manager.name}
                    </div>
                  ))}
              </div>
            )}

            {/* Selected Tags */}
            <div className="mt-2 flex flex-wrap gap-2">
              {ccTo.map((id) => {
                const mgr = managers.find((m) => m.id === id);
                return (
                  <span
                    key={id}
                    className="flex items-center bg-gray-100 text-sm text-gray-700 px-3 py-1 rounded-full"
                  >
                    {mgr?.name || "Unknown"}
                    <button
                      type="button"
                      onClick={() =>
                        setCcTo((prev) => prev.filter((ccId) => ccId !== id))
                      }
                      className="ml-2 text-gray-500 hover:text-red-500"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              REASON
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              placeholder="Please specify the reason for regularization"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            />
          </div>

          <div className="flex justify-end pt-4 space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-300 rounded-lg hover:bg-gray-400 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg hover:from-blue-700 hover:to-indigo-800 focus:outline-none"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttendanceRegularizeModal;
